import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useEffect, useState, useRef, useCallback } from "react";
import { HiX } from "react-icons/hi";
import styles from "./ResponsiveModal.module.css";

const ANIMATION_DURATION = 220;

const ResponsiveModal = ({
  isOpen,
  onClose,
  title = "Detalhes",
  subtitle = "modal",
  children,
  primaryAction,
  secondaryAction,
  size = "md",
  contentClassName = "",
  zIndex = 1000,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [contentHeight, setContentHeight] = useState(null);
  const [contentMaxHeight, setContentMaxHeight] = useState(null);
  const contentRef = useRef(null);
  const contentInnerRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      document.body.removeAttribute("style");
      setTimeout(() => setIsVisible(false), ANIMATION_DURATION);
    }
    return () => document.body.removeAttribute("style");
  }, [isOpen]);

  const measureHeights = useCallback(() => {
    if (!contentInnerRef.current || !headerRef.current || !contentRef.current) {
      return;
    }
    if (window.innerWidth <= 768) {
      setContentHeight(null);
      setContentMaxHeight(null);
      return;
    }

    const headerHeight =
      headerRef.current.getBoundingClientRect().height || 0;
    const maxAvailable = Math.max(
      220,
      Math.floor(window.innerHeight * 0.8 - headerHeight)
    );
    const contentHeightRaw = contentInnerRef.current.scrollHeight || 0;
    const computed = window.getComputedStyle(contentRef.current);
    const paddingTop = parseFloat(computed.paddingTop || "0") || 0;
    const paddingBottom = parseFloat(computed.paddingBottom || "0") || 0;
    const nextHeight = Math.min(
      contentHeightRaw + paddingTop + paddingBottom,
      maxAvailable
    );

    setContentHeight(Math.max(0, Math.ceil(nextHeight)));
    setContentMaxHeight(Math.max(0, Math.ceil(maxAvailable)));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setContentHeight(null);
      setContentMaxHeight(null);
      return undefined;
    }

    const raf = requestAnimationFrame(measureHeights);
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measureHeights);
    });

    if (contentInnerRef.current) observer.observe(contentInnerRef.current);
    window.addEventListener("resize", measureHeights);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", measureHeights);
    };
  }, [isOpen, measureHeights, children]);

  if (!isVisible && !isOpen) {
    return null;
  }

  const overlayState = isOpen ? styles.visible : styles.hidden;
  const frameState = isOpen ? styles.enter : "";

  const widthClass =
    size === "xs"
      ? styles.xs
      : size === "sm"
      ? styles.small
      : size === "lg"
      ? styles.large
      : size === "xl"
      ? styles.xlarge
      : styles.medium;

  const handlePrimary = () => primaryAction?.onClick?.();
  const handleSecondary = () => secondaryAction?.onClick?.();

  const modalContent = (
    <div
      className={`${styles.overlay} ${overlayState}`}
      style={{
        transitionDuration: `${ANIMATION_DURATION}ms`,
        zIndex,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`${styles.frame} ${frameState} ${widthClass}`}
        style={{ transitionDuration: `${ANIMATION_DURATION}ms` }}
      >
        <div className={styles.headerWrap} ref={headerRef}>
          <div className={styles.headerIcon}>✶</div>
          <div className={styles.headerText}>
            <div className={styles.label}>{subtitle.toUpperCase()}</div>
            <div className={styles.headerSide}>
              <h3 className={styles.title}>{title}</h3>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar modal"
          >
            <HiX />
          </button>
        </div>

        <div
          className={`${styles.content} ${contentClassName}`}
          ref={contentRef}
          style={{
            height: contentHeight ? `${contentHeight}px` : undefined,
            maxHeight: contentMaxHeight ? `${contentMaxHeight}px` : undefined,
          }}
        >
          <div ref={contentInnerRef}>
            <div className={styles.bodySpacing}>{children}</div>
            {(primaryAction || secondaryAction) && (
              <div className={styles.actions}>
                {secondaryAction && (
                  <button
                    type="button"
                    className={`${styles.secondary}`}
                    onClick={handleSecondary}
                    disabled={secondaryAction.disabled}
                  >
                    {secondaryAction.label}
                  </button>
                )}
                {primaryAction && (
                  <button
                    type={primaryAction.type || "button"}
                    className={`${styles.primary} ${
                      primaryAction.className || ""
                    }`}
                    onClick={handlePrimary}
                    disabled={primaryAction.disabled}
                  >
                    {primaryAction.label}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

ResponsiveModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    className: PropTypes.string,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  contentClassName: PropTypes.string,
  zIndex: PropTypes.number,
};

export default ResponsiveModal;
